import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import { LeftNavigationBar } from 'screens/Main/components/LeftNavigationBar/LeftNavigationBar';
import { TopNavigationBar } from 'screens/Main/components/TopNavigationBar/TopNavigationBar';
import Footer from 'components/Footer/Footer';
import { MainRoutesProps } from 'routes';
import CardPrintingsHeader from 'components/CardPrintingsHeader/CardPrintingsHeader';
import CardPrintingsContainer from 'components/CardPrintingsContainer/CardPrintingsContainer';
import GenericGroupContainer from 'components/GenericGroupContainer/GenericGroupContainer';
import { Points } from 'components/Points/Points';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { useSearchCardsQuery, useGetCardPrintingsQuery, useGetCardByIdQuery } from 'store/services/cards/search/cardsSearchApi';
import { 
  useAddFromPrintingsMutation, 
  useUpdateWantListPreferencesMutation,
  useGetWantListEntryQuery,
  useCardToWantListQuantityUpdateMutation,
  type AddFromPrintingsRequest 
} from 'store/services/collectionList/wantList/wantListApi';
import { decimalToPoints } from 'utils/decimalUtils';
import { checkEndpointHealth, getCurrentUserId } from 'utils/backendHealthCheck';
import { AssetUrls } from 'config/assets';
import SecureStorage from 'utils/secureStorage';
import { 
  showError, 
  showCardAddedSuccess, 
  showAuthError,
  showValidationError 
} from 'utils/toastNotifications';
import './ViewCardPrintings.css';

interface Props {
  currentTab: MainRoutesProps;
}

interface CardPrinting {
  id: number;
  name: string;
  artist: string;
  imageUrl: string;
  price: any;
  scryfallId?: string;
  imageUris?: any;
  setName?: string;
  collectorNumber?: string;
  foil?: boolean;
  nonFoil?: boolean;
  borderColor?: string;
}

interface GroupedCards {
  groupKey: string;
  groupLabel: string;
  cards: CardPrinting[];
  minPrice: number | null;
  maxPrice: number | null;
}

export default function ViewCardPrintings({ currentTab }: Props) {
  const { cardName } = useParams<{ cardName: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editWantId = searchParams.get('editWantId');
  const cardIdParam = searchParams.get('cardId');
  const isEditMode = !!editWantId;

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Initialize with empty set for edit mode, wait for data to load
  const [selectedCards, setSelectedCards] = useState<Set<number>>(new Set());
  const [groupBy, setGroupBy] = useState<string>('Artist');
  const [removedGroups, setRemovedGroups] = useState<Set<string>>(new Set());
  const [priceFilter, setPriceFilter] = useState<{ min: number | null; max: number | null }>({ min: null, max: null });
  const [quantity, setQuantity] = useState<number>(4);
  const [hasInitializedFromEdit, setHasInitializedFromEdit] = useState(false);
  const [foilPreferences, setFoilPreferences] = useState<string[]>(['foil', 'nonfoil']);
  const [languagePreferences, setLanguagePreferences] = useState<string[]>(['EN']);
  const [conditionPreferences, setConditionPreferences] = useState<string[]>(['NM', 'LP', 'MP']);
  const [addFromPrintings, { isLoading: isAddingToWantList }] = useAddFromPrintingsMutation();
  const [updateWantListPreferences, { isLoading: isUpdatingWantList }] = useUpdateWantListPreferencesMutation();
  const [updateQuantity] = useCardToWantListQuantityUpdateMutation();
  
  // Fetch existing want list entry if in edit mode
  const { data: existingWantEntry, isLoading: isLoadingWantEntry } = useGetWantListEntryQuery(
    editWantId || '',
    { skip: !editWantId }
  );
  
  // Mobile detection effect
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Decode the card name from URL
  const decodedCardName = cardName ? decodeURIComponent(cardName) : '';

  // If we have a cardId from navigation, use it directly; otherwise search by name
  const shouldSearch = !cardIdParam && decodedCardName;

  // Search for card by name only if we don't have a cardId
  const { data: searchResults, isLoading: isSearching, error: searchError } = useSearchCardsQuery(
    { name: decodedCardName, exact: true, limit: 1 },
    { skip: !shouldSearch }
  );

  // Fetch card details when we have a cardId to get the oracle ID
  const { data: cardDetails } = useGetCardByIdQuery(
    cardIdParam || '',
    { skip: !cardIdParam }
  );

  // Get the card ID either from the URL parameter or from search results
  const firstSearchResult = searchResults?.cards?.[0];
  const firstCardId = cardIdParam || firstSearchResult?.id;
  const searchOracleId = firstSearchResult?.oracleId;

  // Then fetch all printings using that ID (which includes price data)
  const { data: printingsData, isLoading: isLoadingPrintings, error: printingsError } = useGetCardPrintingsQuery(
    firstCardId || '',
    { skip: !firstCardId }
  );
  
  // Combine loading states (only check search loading if we're actually searching)
  const isLoading = (shouldSearch && isSearching) || isLoadingPrintings;
  const error = (shouldSearch && searchError) || printingsError;
  
  // Initialize selected cards and preferences when data loads
  useEffect(() => {
    // Don't initialize anything until we have all the data we need
    if (!printingsData) {
      return;
    }
    
    if (isEditMode) {
      // In edit mode, wait for the want entry to load
      if (isLoadingWantEntry) {
        return;
      }
      
      if (!existingWantEntry) {
        return;
      }
      
      if (!hasInitializedFromEdit) {
        
        // In edit mode, select only cards matching the acceptable sets or specific IDs
        const matchingCardIds = new Set<number>();
        
        // First check if we have specific printing IDs (most precise)
        const acceptedPrintingIds = (existingWantEntry as any).acceptedPrintingIds || [];
        const acceptableSetCodes = (existingWantEntry as any).acceptableSetCodes || [];
        const acceptableSets = existingWantEntry.acceptableSets || [];
        
        console.log('Edit mode - checking for cards to select:', {
          acceptedPrintingIds,
          acceptableSetCodes,
          acceptableSets,
          printingsDataLength: printingsData.length
        });
        
        // Check if this is an "Any Set" entry (empty acceptableSets means accept any version)
        const isAnySet = acceptableSets.length === 0 && acceptedPrintingIds.length === 0 && acceptableSetCodes.length === 0;
        
        if (isAnySet) {
          // "Any Set" mode - select ALL cards
          console.log('Any Set mode detected - selecting all cards');
          printingsData.forEach((card: any) => {
            matchingCardIds.add(card.id);
          });
        } else if (acceptedPrintingIds.length > 0) {
          // If we have specific printing IDs, only select those exact cards
          printingsData.forEach((card: any) => {
            if (acceptedPrintingIds.includes(card.id)) {
              console.log(`Selecting card by ID: ${card.id} - ${card.setName || card.set_name}`);
              matchingCardIds.add(card.id);
            }
          });
        } else {
          // Fallback to set-based matching if no specific IDs
          printingsData.forEach((card: any) => {
            const cardSetId = card.setId || card.set_id;
            const cardSetName = card.setName || card.set_name || card.set;
            
            if ((cardSetId && acceptableSetCodes.includes(cardSetId)) || 
                (cardSetName && acceptableSets.includes(cardSetName))) {
              matchingCardIds.add(card.id);
            }
          });
        }
        
        console.log('Selected card IDs:', Array.from(matchingCardIds));
        setSelectedCards(matchingCardIds);
        
        // Set quantity from existing entry
        setQuantity(existingWantEntry.quantity || 1);
        
        // Set foil preferences from existing entry
        const foilPrefs: string[] = [];
        if (existingWantEntry.acceptFoil) foilPrefs.push('foil');
        if (existingWantEntry.acceptNonFoil) foilPrefs.push('nonfoil');
        // Always set foil preferences, even if empty (though that shouldn't happen)
        setFoilPreferences(foilPrefs.length > 0 ? foilPrefs : ['nonfoil']);
        
        // Set language preferences from existing entry
        if (existingWantEntry.acceptableLanguages && existingWantEntry.acceptableLanguages.length > 0) {
          setLanguagePreferences(existingWantEntry.acceptableLanguages);
        }
        
        // Set condition preferences from existing entry - use acceptableConditions if available
        if (existingWantEntry.acceptableConditions && existingWantEntry.acceptableConditions.length > 0) {
          // Convert backend format to frontend format if needed
          const conditionMap: { [key: string]: string } = {
            'NEAR_MINT': 'NM',
            'SLIGHTLY_PLAYED': 'LP',  // LP = Lightly Played
            'MODERATELY_PLAYED': 'MP',
            'HEAVILY_PLAYED': 'HP',
            'DAMAGED': 'DMG'
          };
          const conditions = existingWantEntry.acceptableConditions.map((c: string) => conditionMap[c] || c);
          setConditionPreferences(conditions);
        } else if (existingWantEntry.minCondition) {
          // Fallback for old minCondition field
          const conditionMap: { [key: string]: string[] } = {
            'NEAR_MINT': ['NM'],
            'SLIGHTLY_PLAYED': ['NM', 'LP'],
            'MODERATELY_PLAYED': ['NM', 'LP', 'MP'],
            'HEAVILY_PLAYED': ['NM', 'LP', 'MP', 'HP'],
            'DAMAGED': ['NM', 'LP', 'MP', 'HP', 'DMG']
          };
          const conditions = conditionMap[existingWantEntry.minCondition] || ['NM', 'LP', 'MP'];
          setConditionPreferences(conditions);
        }
        
        // Mark that we've initialized from edit mode
        setHasInitializedFromEdit(true);
      }
    } else {
      // Not in edit mode - select all cards by default for new entries
      if (!hasInitializedFromEdit) {
        const allCardIds = new Set(printingsData.map((card: any) => card.id));
        setSelectedCards(allCardIds);
        setHasInitializedFromEdit(true);
      }
    }
  }, [printingsData, isEditMode, existingWantEntry, hasInitializedFromEdit, isLoadingWantEntry]);
  
  // Calculate overall min and max prices from all cards
  const { overallMinPrice, overallMaxPrice } = useMemo(() => {
    if (!printingsData || printingsData.length === 0) {
      return { overallMinPrice: 0, overallMaxPrice: 0 };
    }
    
    let minPrice: number | null = null;
    let maxPrice: number | null = null;
    
    printingsData.forEach((card: any) => {
      let price: number | null = null;
      
      // Get price (prefer non-foil)
      if (card.price) {
        if (card.price.usd !== null && card.price.usd !== undefined) {
          price = decimalToPoints(card.price.usd);
        } else if (card.price.usdFoil !== null && card.price.usdFoil !== undefined) {
          price = decimalToPoints(card.price.usdFoil);
        }
      } else if (card.prices) {
        if (card.prices.usd !== null && card.prices.usd !== undefined) {
          price = decimalToPoints(card.prices.usd);
        } else if (card.prices.usdFoil !== null && card.prices.usdFoil !== undefined) {
          price = decimalToPoints(card.prices.usdFoil);
        }
      }
      
      // Fallback to pointValue if no price data
      if (!price && card.pointValue) {
        price = card.pointValue;
      }
      
      if (price && price > 0) {
        if (minPrice === null || price < minPrice) minPrice = price;
        if (maxPrice === null || price > maxPrice) maxPrice = price;
      }
    });
    
    return {
      overallMinPrice: minPrice || 0,
      overallMaxPrice: maxPrice || 0
    };
  }, [printingsData]);
  
  // Group cards by selected criteria and sort by count
  const groupedCards = useMemo((): GroupedCards[] => {
    if (!printingsData) return [];
    
    const groups: { [key: string]: CardPrinting[] } = {};
    
    // Group cards by selected criteria
    printingsData.forEach((card: any) => {
      let groupKey: string;
      
      if (groupBy === 'Border') {
        groupKey = card.borderColor || card.border_color || 'unknown';
      } else {
        // Default to Artist grouping
        groupKey = card.artist || 'Unknown Artist';
      }
      
      
      // Get the image URL
      let imageUrl = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/Search/image-coming-soon.png';
      if (card.imageUris?.normal) {
        imageUrl = card.imageUris.normal;
      } else if (card.image_uris?.normal) {
        imageUrl = card.image_uris.normal;
      } else if (card.scryfallId) {
        // Use AssetUrls helper to construct CDN URL
        imageUrl = AssetUrls.cards.getCardImage(card.scryfallId, 'normal');
      }
      
      const cardPrinting: CardPrinting = {
        id: card.id,
        name: card.name,
        artist: card.artist || 'Unknown Artist',
        imageUrl: imageUrl,
        price: card.price || card.prices,  // Try both price and prices
        scryfallId: card.scryfallId,
        imageUris: card.imageUris,
        setName: card.setName || card.set_name,
        collectorNumber: card.collectorNumber || card.collector_number,
        foil: card.foil,
        nonFoil: card.nonFoil || card.nonfoil,
        borderColor: card.borderColor || card.border_color,
        pointValue: card.pointValue,  // Add pointValue as fallback
        normal_fallbacks: card.normal_fallbacks  // Include fallback URLs from API
      } as any;
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(cardPrinting);
    });
    
    // Convert to array and calculate min/max prices
    const groupedArray: GroupedCards[] = Object.entries(groups).map(([groupKey, cards]) => {
      let minPrice: number | null = null;
      let maxPrice: number | null = null;
      
      cards.forEach(card => {
        // Get price (prefer non-foil)
        let price: number | null = null;
        // Handle price as an array (as per MTGCard interface)
        const priceData = Array.isArray(card.price) && card.price.length > 0 ? card.price[0] : card.price;
        
        if (priceData) {
          if (priceData.usd !== null && priceData.usd !== undefined) {
            price = decimalToPoints(priceData.usd);
          } else if (priceData.usdFoil !== null && priceData.usdFoil !== undefined) {
            price = decimalToPoints(priceData.usdFoil);
          }
        }
        
        // Fallback to pointValue if no price data
        if (!price && (card as any).pointValue) {
          price = (card as any).pointValue;
        }
        
        if (price && price > 0) {
          if (minPrice === null || price < minPrice) minPrice = price;
          if (maxPrice === null || price > maxPrice) maxPrice = price;
        }
      });
      
      // Sort cards by price (descending - highest price first)
      cards.sort((a, b) => {
        let priceA: number = 0;
        let priceB: number = 0;
        
        // Get price for card A
        const priceDataA = Array.isArray(a.price) && a.price.length > 0 ? a.price[0] : a.price;
        if (priceDataA) {
          if (priceDataA.usd !== null && priceDataA.usd !== undefined) {
            const convertedPrice = decimalToPoints(priceDataA.usd);
            if (convertedPrice !== null) priceA = convertedPrice;
          } else if (priceDataA.usdFoil !== null && priceDataA.usdFoil !== undefined) {
            const convertedPrice = decimalToPoints(priceDataA.usdFoil);
            if (convertedPrice !== null) priceA = convertedPrice;
          }
        }
        if (!priceA && (a as any).pointValue) {
          priceA = (a as any).pointValue;
        }
        
        // Get price for card B
        const priceDataB = Array.isArray(b.price) && b.price.length > 0 ? b.price[0] : b.price;
        if (priceDataB) {
          if (priceDataB.usd !== null && priceDataB.usd !== undefined) {
            const convertedPrice = decimalToPoints(priceDataB.usd);
            if (convertedPrice !== null) priceB = convertedPrice;
          } else if (priceDataB.usdFoil !== null && priceDataB.usdFoil !== undefined) {
            const convertedPrice = decimalToPoints(priceDataB.usdFoil);
            if (convertedPrice !== null) priceB = convertedPrice;
          }
        }
        if (!priceB && (b as any).pointValue) {
          priceB = (b as any).pointValue;
        }
        
        // Sort in descending order (highest price first)
        return priceB - priceA;
      });
      
      // Format the label based on grouping type
      let groupLabel = groupKey;
      if (groupBy === 'Border') {
        // Format border color names
        const borderLabels: { [key: string]: string } = {
          'black': 'Black Border',
          'white': 'White Border',
          'silver': 'Silver Border',
          'gold': 'Gold Border',
          'borderless': 'Borderless',
          'unknown': 'Unknown Border'
        };
        groupLabel = borderLabels[groupKey] || (groupKey.charAt(0).toUpperCase() + groupKey.slice(1) + ' Border');
      }
      
      return {
        groupKey,
        groupLabel,
        cards,
        minPrice,
        maxPrice
      };
    });
    
    // Sort by number of cards (descending)
    groupedArray.sort((a, b) => b.cards.length - a.cards.length);
    
    return groupedArray;
  }, [printingsData, groupBy]);
  
  const handleToggleCard = (cardId: number) => {
    const newSelected = new Set(selectedCards);
    if (newSelected.has(cardId)) {
      newSelected.delete(cardId);
    } else {
      newSelected.add(cardId);
    }
    setSelectedCards(newSelected);
  };
  
  const handleRemoveAll = (groupKey: string) => {
    const newSelected = new Set(selectedCards);
    const group = groupedCards.find(g => g.groupKey === groupKey);
    if (group) {
      group.cards.forEach(card => {
        newSelected.delete(card.id);
      });
    }
    setSelectedCards(newSelected);
    
    // Track this group as removed
    const newRemovedGroups = new Set(removedGroups);
    newRemovedGroups.add(groupKey);
    setRemovedGroups(newRemovedGroups);
  };
  
  const handleReset = (groupKey: string) => {
    const newSelected = new Set(selectedCards);
    const group = groupedCards.find(g => g.groupKey === groupKey);
    if (group) {
      // Re-add all cards from this group (they'll be filtered by price in the display logic)
      group.cards.forEach(card => {
        newSelected.add(card.id);
      });
    }
    setSelectedCards(newSelected);
    
    // Remove this group from the removed set
    const newRemovedGroups = new Set(removedGroups);
    newRemovedGroups.delete(groupKey);
    setRemovedGroups(newRemovedGroups);
  };
  
  const handleAddToWantlist = async () => {
    // Check if user is logged in using secure storage
    if (!SecureStorage.isAuthenticated()) {
      showAuthError('Please log in to add cards to your want list.');
      return;
    }
    
    if (!printingsData || printingsData.length === 0) {
      showError('No card data available. Please try refreshing the page.');
      return;
    }
    
    // Handle edit mode
    if (isEditMode && editWantId) {
      const selectedCardData = printingsData.filter((card: any) => 
        selectedCards.has(card.id) && isCardVisibleByPrice(card)
      );
      
      const setNames = selectedCardData.map((card: any) => 
        card.setName || card.set_name || card.set
      );
      const acceptableSets = Array.from(new Set(setNames)).filter(Boolean);
      
      if (acceptableSets.length === 0) {
        showError('Please select at least one printing.');
        return;
      }
      
      // Get the specific card IDs that are selected (same as in add mode)
      const acceptedPrintingIds = selectedCardData.map((card: any) => card.id);
      
      try {
        // Convert condition preferences to the acceptableConditions format
        const conditionMapping: { [key: string]: string } = {
          'NM': 'NEAR_MINT',
          'LP': 'SLIGHTLY_PLAYED',  // LP = Lightly Played
          'MP': 'MODERATELY_PLAYED',
          'HP': 'HEAVILY_PLAYED',
          'DMG': 'DAMAGED'
        };
        
        const acceptableConditions = conditionPreferences.map(c => conditionMapping[c] || c);
        
        console.log('=== UPDATING WANT LIST ===');
        console.log('ID:', editWantId);
        console.log('Quantity being sent:', quantity);
        console.log('Current quantity:', existingWantEntry?.quantity);
        console.log('AcceptedPrintingIds:', acceptedPrintingIds);
        
        // First, update the preferences (without quantity)
        const updateResult = await updateWantListPreferences({
          id: Number(editWantId),
          acceptableSets,
          acceptFoil: foilPreferences.includes('foil'),
          acceptNonFoil: foilPreferences.includes('nonfoil'),
          acceptableLanguages: languagePreferences,
          acceptableConditions,
          acceptedPrintingIds, // Include specific card IDs to preserve exact selections
        }).unwrap();
        
        console.log('Preferences update result:', updateResult);
        
        // Then, if quantity has changed, update it using the quantity-specific endpoint
        if (existingWantEntry && quantity !== existingWantEntry.quantity) {
          const quantityChange = quantity - existingWantEntry.quantity;
          console.log('Updating quantity with change:', quantityChange);
          
          await updateQuantity({
            id: Number(editWantId),
            change: quantityChange,
          }).unwrap();
          
          console.log('Quantity updated successfully');
        }
        
        showCardAddedSuccess(decodedCardName, quantity);
        
        // Navigate back to want list
        navigate('/my-cards');
      } catch (error) {
        console.error('Failed to update want list:', error);
        showError('Failed to update want list. Please try again.');
      }
      return;
    }
    
    // Get oracle ID - check all possible locations
    const firstPrinting = printingsData[0];
    const oracleId =
      (cardDetails as any)?.oracleId ||  // Check root level first (this is where it actually is)
      searchOracleId ||
      firstPrinting.oracleId ||
      (firstPrinting as any).oracle_id ||
      cardDetails?.card?.oracleId ||  // Keep for backward compatibility
      (firstPrinting as any).oracleID;  // Check different casings

    // The card name should come from the URL parameter, not the printing data
    const cardName = decodedCardName || firstPrinting.name;

    if (!oracleId) {
      console.error('No oracle ID found. Card details:', cardDetails, 'Search oracleId:', searchOracleId, 'Printing data:', firstPrinting);
      showError('Unable to add card - missing card identifier. Please contact support if this persists.');
      return;
    }
    
    // Get the selected cards and their sets
    const selectedCardData = printingsData.filter((card: any) => 
      selectedCards.has(card.id) && isCardVisibleByPrice(card)
    );
    
    // Group selected cards by border color
    const cardsByBorder = selectedCardData.reduce((acc: any, card: any) => {
      const borderColor = card.borderColor || card.border_color || 'black';
      if (!acc[borderColor]) {
        acc[borderColor] = [];
      }
      acc[borderColor].push(card);
      return acc;
    }, {});
    
    // Log border grouping for debugging
    console.log('Selected cards grouped by border:', {
      borders: Object.keys(cardsByBorder),
      counts: Object.entries(cardsByBorder).map(([border, cards]: [string, any]) => 
        `${border}: ${cards.length} cards`
      )
    });
    
    // Log border types for debugging
    const borderTypes = Object.keys(cardsByBorder);
    if (borderTypes.length > 1) {
      console.log(`✓ Multiple border types selected: ${borderTypes.join(', ')}`);
      console.log('Backend now supports multiple want records for different criteria.');
    }
    
    // Extract unique set names from selected cards
    const setNames = selectedCardData.map((card: any) => 
      card.setName || card.set_name || card.set
    );
    const acceptableSets = Array.from(new Set(setNames)).filter(Boolean);
    
    if (acceptableSets.length === 0) {
      showError('Please select at least one printing to add to your want list.');
      return;
    }
    
    // Get the specific card IDs that are selected
    const acceptedPrintingIds = selectedCardData.map((card: any) => card.id);
    
    // Convert condition preferences to backend format
    const conditionMapping: { [key: string]: string } = {
      'NM': 'NEAR_MINT',
      'LP': 'SLIGHTLY_PLAYED',
      'MP': 'MODERATELY_PLAYED',
      'HP': 'HEAVILY_PLAYED',
      'DMG': 'DAMAGED'
    };
    
    const acceptableConditions = conditionPreferences.map(c => conditionMapping[c] || c);
    
    const requestPayload: AddFromPrintingsRequest = {
      cardName: cardName,
      oracleId: oracleId,
      quantity: quantity,
      acceptableSets: acceptableSets,
      acceptableLanguages: languagePreferences, // Use actual language preferences
      acceptFoil: foilPreferences.includes('foil'), // Use actual foil preferences
      acceptNonFoil: foilPreferences.includes('nonfoil'), // Use actual non-foil preferences
      acceptableConditions: acceptableConditions, // Use new granular conditions field
      acceptedPrintingIds: acceptedPrintingIds, // Pass specific card IDs to prevent mismatch
      // TODO: Add borderColors field when backend supports it
      // borderColors: borderTypes,
    };
    
    
    // Quick health check before making request
    const healthCheck = await checkEndpointHealth('/want-list/add-from-printings', true);
    if (!healthCheck.isHealthy && healthCheck.error?.includes('Connection failed')) {
      console.warn('Backend may be unavailable:', healthCheck.error);
    }
    
    try {
      const result = await addFromPrintings(requestPayload).unwrap();
      
      console.log('Successfully added to want list:', result);
      console.log('Response details:', {
        id: result?.id,
        isNew: result?.id ? 'Check if this is a new ID' : 'No ID returned',
        acceptableSets: result?.acceptableSets,
        quantity: result?.quantity
      });
      
      // Show success with details about what was added
      const borderInfo = Object.keys(cardsByBorder).join(', ');
      
      // The backend now properly creates separate want records
      showCardAddedSuccess(cardName, quantity);
      
      // Log details for debugging
      console.log('Want record created successfully:', {
        id: result?.id,
        cardName: cardName,
        sets: acceptableSets,
        borders: borderInfo
      });
    } catch (error: any) {
      console.error('Failed to add to want list:', error);
      console.error('Full error data:', error?.data);
      console.error('Error details:', {
        status: error?.status,
        data: error?.data,
        errors: error?.data?.errors,
        message: error?.data?.message
      });
      
      // Build error message from validation errors if available
      if (error?.status === 500) {
        // Check if this might be a user ID issue
        const userId = getCurrentUserId();
        if (!userId) {
          showAuthError('Session expired or invalid. Please log in again.');
          console.error('No user ID found in token - user may need to log in');
        } else if (userId > 5) {
          showAuthError('Authentication issue detected. Please log out and log back in to refresh your session.');
          console.error('Possible invalid user ID in token:', userId);
        } else {
          showError('Server error occurred. Please check backend logs or contact support.');
          console.error('Backend returned 500 for valid user ID:', userId);
        }
        console.error('Backend server error - check backend logs for details');
        // Log the full request for debugging
        console.error('Failed request payload was:', requestPayload);
        console.error('Current user ID from token:', userId);
        
        // Save failed request to localStorage for recovery
        try {
          const failedRequests = JSON.parse(localStorage.getItem('failedWantListRequests') || '[]');
          failedRequests.push({
            timestamp: new Date().toISOString(),
            payload: requestPayload,
            error: 'Server Error 500'
          });
          // Keep only last 10 failed requests
          if (failedRequests.length > 10) {
            failedRequests.shift();
          }
          localStorage.setItem('failedWantListRequests', JSON.stringify(failedRequests));
          console.log('Failed request saved to localStorage for recovery');
        } catch (e) {
          console.error('Could not save failed request to localStorage:', e);
        }
      } else if (error?.data?.errors && Array.isArray(error.data.errors)) {
        const errorMessages = error.data.errors.map((e: any) => e.msg || e.message);
        showValidationError(errorMessages);
      } else if (error?.data?.message) {
        showError(error.data.message);
      } else {
        showError(`Failed to add ${cardName} to want list. Please try again.`);
      }
    }
  };
  
  // Helper function to get card price
  const getCardPrice = (card: any): number | null => {
    let price: number | null = null;
    
    // Handle price as an array (as per MTGCard interface)
    const priceData = Array.isArray(card.price) && card.price.length > 0 ? card.price[0] : card.price;
    
    if (priceData) {
      if (priceData.usd !== null && priceData.usd !== undefined) {
        price = decimalToPoints(priceData.usd);
      } else if (priceData.usdFoil !== null && priceData.usdFoil !== undefined) {
        price = decimalToPoints(priceData.usdFoil);
      }
    }
    
    // Fallback to pointValue if no price data
    if (!price && (card as any).pointValue) {
      price = (card as any).pointValue;
    }
    
    return price;
  };
  
  // Check if a card should be visible based on price filter
  const isCardVisibleByPrice = (card: any): boolean => {
    const cardPrice = getCardPrice(card);
    
    // If card has no price, hide it when any price filter is applied
    if (cardPrice === null || cardPrice === 0) {
      return priceFilter.min === null && priceFilter.max === null;
    }
    
    // Check min price filter
    if (priceFilter.min !== null && cardPrice < priceFilter.min) {
      return false;
    }
    
    // Check max price filter
    if (priceFilter.max !== null && cardPrice > priceFilter.max) {
      return false;
    }
    
    return true;
  };
  
  const handlePriceChange = (min: number, max: number) => {
    setPriceFilter({ min, max });
  };

  if (isLoading) {
    return (
      <div className="view-card-printings-container">
        {/* Mobile Menu Overlay - click to close */}
        {isMobile && isMobileMenuOpen && (
          <div
            className="mobile-menu-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <LeftNavigationBar
          currentTab={currentTab}
          isMobile={isMobile}
          isMobileMenuOpen={isMobileMenuOpen}
          onMenuClose={() => setIsMobileMenuOpen(false)}
        />
        <div className="view-card-printings-content">
          <div className="view-card-printings-bg" />
          <div className="view-card-printings-topnav">
            <TopNavigationBar
              title="View Card Printings"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />
          </div>
          <div className="view-card-printings-main">
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '4rem',
              gap: '1rem'
            }}>
              <LoadingIndicator />
              <div style={{ color: 'white', fontSize: '16px', marginTop: '1rem' }}>
                Loading printings for {decodedCardName}...
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || (!printingsData && !isLoading)) {
    return (
      <div className="view-card-printings-container">
        {/* Mobile Menu Overlay - click to close */}
        {isMobile && isMobileMenuOpen && (
          <div
            className="mobile-menu-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <LeftNavigationBar
          currentTab={currentTab}
          isMobile={isMobile}
          isMobileMenuOpen={isMobileMenuOpen}
          onMenuClose={() => setIsMobileMenuOpen(false)}
        />
        <div className="view-card-printings-content">
          <div className="view-card-printings-bg" />
          <div className="view-card-printings-topnav">
            <TopNavigationBar
              title="View Card Printings"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />
          </div>
          <div className="view-card-printings-main">
            <div style={{ padding: '2rem', textAlign: 'center', color: 'white' }}>
              No printings found for {decodedCardName}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get background image from first card
  const firstCard = printingsData?.[0] as any;
  const backgroundImage = firstCard?.imageUris?.art_crop;

  return (
    <div className="view-card-printings-container">
      {/* Mobile Menu Overlay - click to close */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <LeftNavigationBar
        currentTab={currentTab}
        isMobile={isMobile}
        isMobileMenuOpen={isMobileMenuOpen}
        onMenuClose={() => setIsMobileMenuOpen(false)}
      />

      {/* Content Area */}
      <div className="view-card-printings-content">
        {/* Background with Blur */}
        <div
          className="view-card-printings-bg"
          style={backgroundImage ? {
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(110px)'
          } : undefined}
        />

        {/* Top Navigation */}
        <div className="view-card-printings-topnav">
          <TopNavigationBar
            title={`${decodedCardName} - All Printings`}
            isMobileMenuOpen={isMobileMenuOpen}
            onToggleMobileMenu={toggleMobileMenu}
          />
        </div>

        {/* Main Body */}
        <div className="view-card-printings-main">
          <CardPrintingsHeader
            selectedCount={Array.from(selectedCards).filter(cardId => {
              // Find the card and check if it passes price filter
              const card = printingsData?.find((c: any) => c.id === cardId);
              return card && isCardVisibleByPrice(card);
            }).length}
            onAddToWantlist={handleAddToWantlist}
            onPriceChange={handlePriceChange}
            buttonText={isEditMode ? 'Update Want List' : 'Add to Want List'}
            headerTitle={isEditMode ? 'Update desired versions' : undefined}
            headerSubtitle={isEditMode ? "Select versions of this card that you'd like to receive to update the want list record for this card." : undefined}
            defaultQuantity={quantity}
            initialFoil={isEditMode ? foilPreferences.includes('foil') : undefined}
            initialRegular={isEditMode ? foilPreferences.includes('nonfoil') : undefined}
            initialLanguages={isEditMode ? languagePreferences : undefined}
            initialConditions={isEditMode ? conditionPreferences : undefined}
            isAddingToWantlist={isEditMode ? isUpdatingWantList : isAddingToWantList}
            onGroupChange={(val) => {
              setGroupBy(val);
              setRemovedGroups(new Set()); // Clear removed groups when changing groupBy
            }}
            onQtyChange={(val) => {
              console.log('Quantity changed to:', val);
              setQuantity(val);
            }}
            onFiltersChange={(filters: any) => {
              // Map the filter values to our state
              if (filters.foil && filters.foil.length > 0) {
                const foilMap: { [key: string]: string } = {
                  'Foil': 'foil',
                  'Regular': 'nonfoil'
                };
                setFoilPreferences(filters.foil.map((f: any) => foilMap[f] || f.toLowerCase()));
              }
              if (filters.languages && filters.languages.length > 0) {
                setLanguagePreferences(filters.languages);
              }
              if (filters.conditions && filters.conditions.length > 0) {
                setConditionPreferences(filters.conditions);
              }
            }}
            minPrice={overallMinPrice}
            maxPrice={overallMaxPrice}
          />

          {/* Card Printings Container with Multiple Groups */}
          <CardPrintingsContainer>
            {groupedCards.map((group) => (
              <GenericGroupContainer
                key={group.groupKey}
                title={`${group.cards.length} version${group.cards.length !== 1 ? 's' : ''} available`}
                lowPrice={group.minPrice || 0}
                highPrice={group.maxPrice || 0}
                groupName={group.groupLabel}
                onRemoveAll={() => handleRemoveAll(group.groupKey)}
                onReset={() => handleReset(group.groupKey)}
                isRemoved={removedGroups.has(group.groupKey)}
              >
                <div className="card-grid">
                  {group.cards.map((card) => {
                    // Calculate price for display
                    let cardPrice: number | null = null;
                    // Handle price as an array (as per MTGCard interface)
                    const priceData = Array.isArray(card.price) && card.price.length > 0 ? card.price[0] : card.price;
                    
                    if (priceData) {
                      if (priceData.usd !== null && priceData.usd !== undefined) {
                        cardPrice = decimalToPoints(priceData.usd);
                      } else if (priceData.usdFoil !== null && priceData.usdFoil !== undefined) {
                        cardPrice = decimalToPoints(priceData.usdFoil);
                      }
                    }
                    
                    // Fallback to pointValue if no price data
                    if (!cardPrice && (card as any).pointValue) {
                      cardPrice = (card as any).pointValue;
                    }
                    
                    // Check if card is visible based on price filter
                    const isVisibleByPrice = isCardVisibleByPrice(card);
                    
                    // Card is "selected" (visible) only if it's both selected AND passes price filter
                    const isCardSelected = selectedCards.has(card.id) && isVisibleByPrice;
                    
                    return (
                      <div 
                        key={card.id} 
                        className={`card-item ${isCardSelected ? 'selected' : ''}`}
                        onClick={() => handleToggleCard(card.id)}
                        style={{ cursor: 'pointer', position: 'relative' }}
                      >
                        {!isCardSelected && (
                          <div className="deselected-overlay">
                            <img 
                              className="heart-icon broken"
                              src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/ViewCard/want-no.svg"
                              alt="Not wanted"
                            />
                            <img 
                              className="heart-icon unbroken"
                              src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/heart.svg"
                              alt="Add to want"
                            />
                          </div>
                        )}
                        <img
                          className="card-item-image"
                          src={card.imageUrl}
                          alt={`${card.name} - ${card.setName}`}
                          onError={(e) => {
                            const img = e.currentTarget;
                            const currentSrc = img.src;
                            
                            // First try backend-provided fallback URLs if available
                            const fallbacks = (card as any).normal_fallbacks;
                            if (fallbacks && fallbacks.length > 0 && !img.dataset.fallbackAttempted) {
                              img.dataset.fallbackAttempted = 'true';
                              img.src = fallbacks[0];
                              return;
                            }
                            
                            // Otherwise, try different image formats using scryfallId
                            if (card.scryfallId && !img.dataset.formatFallbackComplete) {
                              // Determine current format from URL
                              if (currentSrc.includes('/normal/')) {
                                // Normal format failed, try small
                                img.src = AssetUrls.cards.getCardImage(card.scryfallId, 'small');
                                return;
                              } else if (currentSrc.includes('/small/')) {
                                // Small format failed, try large
                                img.src = AssetUrls.cards.getCardImage(card.scryfallId, 'large');
                                return;
                              } else if (currentSrc.includes('/large/')) {
                                // Large format failed, mark as complete and use placeholder
                                img.dataset.formatFallbackComplete = 'true';
                              }
                            }
                            
                            // If all fallbacks failed, use placeholder
                            img.src = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/Search/image-coming-soon.png';
                          }}
                        />
                        <div className="card-item-points">
                          {cardPrice !== null && cardPrice > 0 ? (
                            <Points points={cardPrice} type="small" />
                          ) : (
                            <div style={{ 
                              position: 'relative',
                              padding: '2px',
                              borderRadius: '24px',
                              background: 'linear-gradient(90deg, #f6e3bf, #634f21)',
                              height: '18px',
                              width: '65px'
                            }}>
                              <div style={{
                                display: 'flex', 
                                alignItems: 'center',
                                position: 'absolute',
                                top: '1px',
                                bottom: '1px',
                                left: '1px',
                                right: '1px',
                                padding: '1.5px',
                                borderRadius: '24px',
                                backgroundColor: 'rgba(0, 0, 0, 0.85)'
                              }}>
                                <img
                                  src={AssetUrls.icons.profile.coin}
                                  width={16}
                                  height={16}
                                  alt="coin-image"
                                  style={{ paddingLeft: '1px' }}
                                />
                                <span style={{ 
                                  flex: 1,
                                  textAlign: 'center',
                                  fontSize: '11px',
                                  color: 'var(--gold)',
                                  fontFamily: 'Georgia',
                                  marginLeft: '2px',
                                  marginRight: '2px',
                                  paddingBottom: '2px'
                                }}>???</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </GenericGroupContainer>
            ))}
          </CardPrintingsContainer>
        </div>

        {/* Footer */}
        <div className="view-card-printings-footer">
          <Footer />
        </div>
      </div>
    </div>
  );
}
